import type { CommandEnvelope, Workspace } from './model'

interface CommandLane {
  tail: Promise<void>
  pending: number
  localRevisions: Map<number, number>
}

/**
 * Serialize this provider's commands, without rebasing over another writer.
 * An enqueued revision may advance only through successful local predecessors
 * in the same pending lane. Payloads and their entity revisions stay untouched.
 */
export function createLocalCommandQueue(execute: (command: CommandEnvelope) => Promise<Workspace>) {
  const lanes = new Map<string, CommandLane>()

  const enqueue = (input: CommandEnvelope): Promise<Workspace> => {
    if (!input.workspaceId) return Promise.reject(new Error('Load the workspace before saving changes.'))
    const command = structuredClone(input), target = command.workspaceId!
    let lane = lanes.get(target)
    if (!lane) {
      lane = { tail: Promise.resolve(), pending: 0, localRevisions: new Map() }
      lanes.set(target, lane)
    }
    const current = lane
    current.pending++
    const task = current.tail.then(async () => {
      let revision = command.expectedRevision
      while (revision !== undefined && current.localRevisions.has(revision)) revision = current.localRevisions.get(revision)!
      const next = await execute({ ...command, expectedRevision: revision })
      // Never treat an external revision gap (or an idempotent no-op) as our own commit.
      if (next.id === target && revision !== undefined && next.revision === revision + 1)
        current.localRevisions.set(revision, next.revision)
      return next
    })
    current.tail = task.then(() => undefined, () => undefined)
    void current.tail.then(() => {
      current.pending--
      if (!current.pending && lanes.get(target) === current) lanes.delete(target)
    })
    return task
  }

  const drain = async (workspaceId?: string) => {
    // Repeat because a command finishing may synchronously enqueue another one.
    while (true) {
      const pending = [...lanes.entries()].filter(([id]) => !workspaceId || id === workspaceId).map(([, lane]) => lane.tail)
      if (!pending.length) return
      await Promise.all(pending)
    }
  }
  return { enqueue, drain }
}
